import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { Profiler, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import cx from 'classnames';
import { scaleLinear } from 'd3';
import graphviz from 'graphviz-wasm';
import { DragDropContext, Draggable, Droppable, } from 'react-beautiful-dnd';
import { Button, ConditionalWrapper, KeyDownProvider, UserPreferences, useParcaContext, useURLState, } from '@parca/components';
import { useContainerDimensions } from '@parca/hooks';
import { selectDarkMode, useAppSelector } from '@parca/store';
import { getNewSpanColor } from '@parca/utilities';
import { Callgraph } from '../';
import { jsonToDot } from '../Callgraph/utils';
import ProfileIcicleGraph from '../ProfileIcicleGraph';
import { SourceView } from '../SourceView';
import Table from '../Table';
import ProfileShareButton from '../components/ProfileShareButton';
import useDelayedLoader from '../useDelayedLoader';
import FilterByFunctionButton from './FilterByFunctionButton';
import { ProfileViewContextProvider } from './ProfileViewContext';
import ViewSelector from './ViewSelector';
import { VisualizationPanel } from './VisualizationPanel';
function arrayEquals(a, b) {
    return (Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => val === b[index]));
}
export const ProfileView = ({ total, filtered, flamegraphData, topTableData, callgraphData, sourceData, sampleUnit, profileSource, queryClient, navigateTo, onDownloadPProf, pprofDownloading, }) => {
    const { ref, dimensions } = useContainerDimensions();
    const [curPath, setCurPath] = useState([]);
    const [rawDashboardItems = ['icicle'], setDashboardItems] = useURLState({
        param: 'dashboard_items',
        navigateTo,
    });
    const [graphvizLoaded, setGraphvizLoaded] = useState(false);
    const [callgraphSVG, setCallgraphSVG] = useState(undefined);
    const [currentSearchString] = useURLState({ param: 'search_string' });
    const dashboardItems = useMemo(() => {
        if (rawDashboardItems !== undefined) {
            return rawDashboardItems;
        }
        return ['icicle'];
    }, [rawDashboardItems]);
    const isDarkMode = useAppSelector(selectDarkMode);
    const isMultiPanelView = dashboardItems.length > 1;
    const { loader, perf } = useParcaContext();
    useEffect(() => {
        // Reset the current path when the profile source changes
        setCurPath([]);
    }, [profileSource]);
    useEffect(() => {
        async function loadGraphviz() {
            await graphviz.loadWASM();
            setGraphvizLoaded(true);
        }
        void loadGraphviz();
    }, []);
    const isLoading = useMemo(() => {
        if (dashboardItems.includes('icicle')) {
            return Boolean(flamegraphData?.loading);
        }
        if (dashboardItems.includes('callgraph')) {
            return Boolean(callgraphData?.loading) || Boolean(callgraphSVG === undefined);
        }
        if (dashboardItems.includes('table')) {
            return Boolean(topTableData?.loading);
        }
        if (dashboardItems.includes('source')) {
            return Boolean(sourceData?.loading);
        }
        return false;
    }, [
        dashboardItems,
        callgraphData?.loading,
        flamegraphData?.loading,
        topTableData?.loading,
        sourceData?.loading,
        callgraphSVG,
    ]);
    const isLoaderVisible = useDelayedLoader(isLoading);
    const maxColor = getNewSpanColor(isDarkMode);
    const minColor = scaleLinear([isDarkMode ? 'black' : 'white', maxColor])(0.3);
    const colorRange = [minColor, maxColor];
    // Note: If we want to further optimize the experience, we could try to load the graphviz layout in the ProfileViewWithData layer
    // and pass it down to the ProfileView. This would allow us to load the layout in parallel with the flamegraph data.
    // However, the layout calculation is dependent on the width and color range of the graph container, which is why it is done at this level
    useEffect(() => {
        async function loadCallgraphSVG(graph, width, colorRange) {
            await setCallgraphSVG(undefined);
            // Translate JSON to 'dot' graph string
            const dataAsDot = await jsonToDot({
                graph,
                width,
                colorRange,
            });
            // Use Graphviz-WASM to translate the 'dot' graph to a 'JSON' graph
            const svgGraph = await graphviz.layout(dataAsDot, 'svg', 'dot');
            await setCallgraphSVG(svgGraph);
        }
        if (graphvizLoaded &&
            callgraphData?.data !== null &&
            callgraphData?.data !== undefined &&
            dimensions?.width !== undefined) {
            void loadCallgraphSVG(callgraphData?.data, dimensions?.width, colorRange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graphvizLoaded, callgraphData?.data]);
    const setNewCurPath = (path) => {
        if (!arrayEquals(curPath, path)) {
            setCurPath(path);
        }
    };
    const getDashboardItemByType = ({ type, isHalfScreen, setActionButtons, }) => {
        switch (type) {
            case 'icicle': {
                return (_jsx(ConditionalWrapper, { condition: perf?.onRender != null, WrapperComponent: Profiler, wrapperProps: {
                        id: 'icicleGraph',
                        onRender: perf?.onRender,
                    }, children: _jsx(ProfileIcicleGraph, { curPath: curPath, setNewCurPath: setNewCurPath, arrow: flamegraphData?.arrow, graph: flamegraphData?.data, total: total, filtered: filtered, sampleUnit: sampleUnit, navigateTo: navigateTo, loading: flamegraphData.loading, setActionButtons: setActionButtons, error: flamegraphData.error, width: dimensions?.width !== undefined
                            ? isHalfScreen
                                ? (dimensions.width - 40) / 2
                                : dimensions.width - 16
                            : 0 }) }));
            }
            case 'callgraph': {
                return callgraphData?.data !== undefined &&
                    callgraphSVG !== undefined &&
                    dimensions?.width !== undefined ? (_jsx(Callgraph, { data: callgraphData.data, svgString: callgraphSVG, sampleUnit: sampleUnit, width: isHalfScreen ? dimensions?.width / 2 : dimensions?.width })) : (_jsx(_Fragment, {}));
            }
            case 'table': {
                return topTableData != null ? (_jsx(Table, { total: total, filtered: filtered, loading: topTableData.loading, data: topTableData.arrow?.record, sampleUnit: sampleUnit, navigateTo: navigateTo, setActionButtons: setActionButtons, currentSearchString: currentSearchString })) : (_jsx(_Fragment, {}));
            }
            case 'source': {
                return sourceData != null ? (_jsx(SourceView, { loading: sourceData.loading, data: sourceData.data, total: total, filtered: filtered, setActionButtons: setActionButtons })) : (_jsx(_Fragment, {}));
            }
            default: {
                return _jsx(_Fragment, {});
            }
        }
    };
    const handleClosePanel = (visualizationType) => {
        const newDashboardItems = dashboardItems.filter(item => item !== visualizationType);
        setDashboardItems(newDashboardItems);
    };
    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (Boolean(destination) && destination?.index !== source.index) {
            const targetItem = draggableId;
            const otherItems = dashboardItems.filter(item => item !== targetItem);
            const newDashboardItems = destination.index < source.index
                ? [targetItem, ...otherItems]
                : [...otherItems, targetItem];
            setDashboardItems(newDashboardItems);
        }
    };
    // TODO: this is just a placeholder, we need to replace with an actually informative and accurate title (cc @metalmatze)
    const profileSourceString = profileSource?.toString();
    const hasProfileSource = profileSource !== undefined && profileSourceString !== '';
    const headerParts = profileSourceString?.split('"') ?? [];
    return (_jsx(KeyDownProvider, { children: _jsxs(ProfileViewContextProvider, { value: { profileSource, sampleUnit }, children: [_jsxs("div", { className: cx('mb-4 flex w-full items-center', hasProfileSource ? 'justify-between' : 'justify-end'), children: [hasProfileSource && (_jsxs("div", { className: "max-w-[300px]", children: [_jsx("div", { className: "text-sm font-medium capitalize", children: headerParts.length > 0 ? headerParts[0].replace(/"/g, '') : '' }), _jsx("div", { className: "text-xs", children: headerParts.length > 1
                                        ? headerParts[headerParts.length - 1].replace(/"/g, '')
                                        : '' })] })), _jsxs("div", { className: "flex items-center justify-end gap-x-2", children: [_jsx(FilterByFunctionButton, { navigateTo: navigateTo }), _jsx(UserPreferences, { customButton: _jsxs(Button, { className: "gap-2", variant: "neutral", children: ["Preferences", _jsx(Icon, { icon: "pajamas:preferences", width: 20 })] }) }), profileSource !== undefined && queryClient !== undefined ? (_jsx(ProfileShareButton, { queryRequest: profileSource.QueryRequest(), queryClient: queryClient })) : null, _jsxs(Button, { className: "gap-2", variant: "neutral", onClick: e => {
                                        e.preventDefault();
                                        onDownloadPProf();
                                    }, disabled: pprofDownloading, children: [pprofDownloading != null && pprofDownloading ? 'Downloading...' : 'Download pprof', _jsx(Icon, { icon: "material-symbols:download", width: 20 })] }), _jsx(ViewSelector, { defaultValue: "", navigateTo: navigateTo, position: -1, placeholderText: "Add panel", icon: _jsx(Icon, { icon: "material-symbols:add", width: 20 }), addView: true, disabled: isMultiPanelView || dashboardItems.length < 1 })] })] }), _jsx("div", { className: "w-full", ref: ref, children: isLoaderVisible ? (_jsx(_Fragment, { children: loader })) : (_jsx(DragDropContext, { onDragEnd: onDragEnd, children: _jsx(Droppable, { droppableId: "droppable", direction: "horizontal", children: provided => (_jsx("div", { ref: provided.innerRef, className: cx('grid w-full gap-2', isMultiPanelView ? 'grid-cols-2' : 'grid-cols-1'), ...provided.droppableProps, children: dashboardItems.map((dashboardItem, index) => {
                                    return (_jsx(Draggable, { draggableId: dashboardItem, index: index, isDragDisabled: !isMultiPanelView, children: (provided, snapshot) => (_createElement("div", { ref: provided.innerRef, ...provided.draggableProps, key: dashboardItem, className: cx('min-h-[200px] w-full rounded p-2 shadow dark:border dark:border-gray-700 dark:bg-gray-700', snapshot.isDragging
                                                ? 'bg-gray-200 dark:bg-gray-500'
                                                : 'bg-white dark:bg-gray-700') },
                                            _jsx(VisualizationPanel, { handleClosePanel: handleClosePanel, isMultiPanelView: isMultiPanelView, dashboardItem: dashboardItem, getDashboardItemByType: getDashboardItemByType, dragHandleProps: provided.dragHandleProps, navigateTo: navigateTo, index: index }))) }, dashboardItem));
                                }) })) }) })) })] }) }));
};
